import { __assign } from "tslib";
import FlakeId from 'flakeid/lib/flakeid';
import { findKey, round } from 'lodash-es';
import { EventOutcomeType, EventTriggerType, OverlayPosition, } from '@marvelapp/core';
import { assertValidEvent } from '@marvelapp/prototype-event-schema';
import { getPositionStyles } from '../components/Overlay';
import { CLICK, HOVER, PINCH_IN, PINCH_OUT, SWIPE_DOWN, SWIPE_LEFT, SWIPE_RIGHT, SWIPE_UP, } from '../constants/hotspotAction';
import { CURRENT_SCREEN_ID, SCREEN_LAYER, SCROLL_CONTAINER_LAYER, } from '../constants/userTest';
import { getCurrentScale, getOverlayProps, getScreenStyles, } from '../selectors';
import { getCurrentOverlayHotspot, getHotspotByID } from '../selectors/hotspot';
import { getCurrentOverlayScreen, getCurrentScreen } from '../selectors/screen';
import { viewportToImage } from './transformCoordinates';
var flake = new FlakeId();
var currentEventId = null;
var startTime = null;
export function startRecording() {
    var event = {
        trigger: {
            type: 'player',
        },
        object: {
            type: 'player',
        },
        outcome: {
            type: EventOutcomeType.StartRecording,
        },
    };
    return makeEvent(event);
}
export function initialScreen(action, state) {
    var id = getCurrentScreen(state).id;
    var event = {
        trigger: {
            type: 'player',
        },
        object: {
            type: 'player',
        },
        outcome: {
            type: EventOutcomeType.ScreenTransition,
            scrollPosition: [0, 0],
            screen: id,
            animation: null,
            reverseAnimation: false,
        },
    };
    return makeEvent(event);
}
export function goalScreen(action, state) {
    var _a = getCurrentScreen(state), id = _a.id, _b = _a.img, width = _b.width, height = _b.height;
    var event = {
        trigger: {
            type: 'player',
        },
        object: {
            type: 'screen',
            id: id,
            width: width,
            height: height,
        },
        outcome: {
            type: EventOutcomeType.GoalReached,
        },
    };
    return makeEvent(event);
}
export function stopRecording() {
    var event = {
        trigger: {
            type: 'player',
        },
        object: {
            type: 'player',
        },
        outcome: {
            type: EventOutcomeType.StopRecording,
        },
    };
    var fullEvent = makeEvent(event);
    startTime = null;
    return fullEvent;
}
// TODO event for hitting goal screen -- doesn't feel like this belongs in here
// But you could argue start/stop don't either -- maybe the player should always
// emit events and user testing handles start/stop/goal?
export function screenTransition(action, state) {
    var trigger = getActionTrigger(action, state, [
        'hotspot',
        'timer',
        'hoverOut',
        'swipeNavigation',
    ]);
    var event = __assign(__assign({}, trigger), { outcome: {
            type: EventOutcomeType.ScreenTransition,
            screen: action.screenID,
            scrollPosition: [
                0,
                action.scrollPos ? round(action.scrollPos.current) : 0,
            ],
            animation: getCleanAnimationName(action.transition),
            reverseAnimation: false,
        } });
    return makeEvent(event);
}
export function overlayTransition(action, state) {
    var trigger = getActionTrigger(action, state, ['hotspot', 'hoverOut']);
    var hotspot = getHotspotByID(state, action.hotspotID);
    var event = __assign(__assign({}, trigger), { outcome: {
            type: EventOutcomeType.OverlayTransition,
            scrollPosition: [0, round(action.scrollPos)],
            screen: hotspot.destinationScreenID,
            animation: getCleanAnimationName(hotspot.transition),
            position: getOverlayCoords(state, hotspot, action),
            reverseAnimation: false,
        } });
    return makeEvent(event);
}
export function removeOverlay(action, state) {
    var trigger = getActionTrigger(action, state, [
        EventOutcomeType.Miss,
        'keyboard',
        'hotspot', // hotspot on overlay that links to current screen
    ]);
    var id = getCurrentScreen(state).id;
    var event = __assign(__assign({}, trigger), { outcome: {
            type: EventOutcomeType.RemoveOverlay,
            screen: id,
            animation: getCleanAnimationName(action.transition),
            reverseAnimation: false,
        } });
    return makeEvent(event);
}
export function navigation(action, state, previousState) {
    var previousScreen = getCurrentScreen(previousState);
    var currentScreen = getCurrentScreen(state);
    if (previousScreen.id !== currentScreen.id) {
        var animation = getCleanAnimationName(state.screen.transition);
        return makeEvent({
            trigger: {
                type: 'navigation',
            },
            object: {
                type: 'player',
            },
            outcome: {
                type: EventOutcomeType.ScreenTransition,
                screen: currentScreen.id,
                scrollPosition: [0, 0],
                animation: animation,
                reverseAnimation: !!state.screen.isTransitionReversed,
            },
        });
    }
    // Screens are the same so must be an overlay transition
    var currentOverlayHotspot = getCurrentOverlayHotspot(state);
    var previousOverlayHotspot = getCurrentOverlayHotspot(previousState);
    if (currentOverlayHotspot &&
        (!previousOverlayHotspot ||
            currentOverlayHotspot !== previousOverlayHotspot)) {
        return makeEvent({
            trigger: {
                type: 'navigation',
            },
            object: {
                type: 'player',
            },
            outcome: {
                type: EventOutcomeType.OverlayTransition,
                screen: currentOverlayHotspot.destinationScreenID,
                scrollPosition: [0, 0],
                animation: getCleanAnimationName(currentOverlayHotspot.transition),
                position: getOverlayCoords(state, currentOverlayHotspot, action),
                reverseAnimation: false,
            },
        });
    }
    if (previousOverlayHotspot && !currentOverlayHotspot) {
        return makeEvent({
            trigger: {
                type: 'navigation',
            },
            object: {
                type: 'player',
            },
            outcome: __assign({ type: EventOutcomeType.RemoveOverlay, screen: currentScreen.id }, getOverlayAnimation(state)),
        });
    }
    throw new Error("Navigation happened but I can't work out the outcome");
}
export function autoScroll(target, action, state) {
    var getter = target === 'screen' ? getCurrentScreen : getCurrentOverlayScreen;
    var layer = getter(state);
    var trigger = getActionTrigger(action, state, ['hotspot']);
    var event = __assign(__assign({}, trigger), { outcome: {
            type: EventOutcomeType.Scroll,
            coords: [0, action.position],
            smooth: action.smoothScroll,
            overlay: target === 'overlay' ? layer.id : null,
            screen: target === 'screen' ? layer.id : null,
        } });
    return makeEvent(event);
}
export function userScroll(action, state) {
    var scrollLeft = action.scrollLeft, scrollTop = action.scrollTop, screenID = action.screenID, targetType = action.targetType;
    var event = {
        trigger: {
            type: 'user',
        },
        object: __assign({ type: targetType, id: screenID }, (targetType === 'screen' && { width: 100, height: 100 })),
        outcome: {
            type: EventOutcomeType.Scroll,
            // convert scroll offset to be relative to the natural scale of the layer (unscaled)
            coords: [unscale(state, scrollLeft), unscale(state, scrollTop)],
            smooth: false,
        },
    };
    return makeEvent(event);
}
export function resize(action, state) {
    var event = {
        trigger: {
            type: 'user',
        },
        object: {
            type: 'player',
        },
        outcome: {
            type: EventOutcomeType.Resize,
            width: Math.round(state.screen.renderedDimensions.width),
            height: Math.round(state.screen.renderedDimensions.height),
        },
    };
    return makeEvent(event);
}
export function moveMouse(action, state) {
    var event = {
        trigger: {
            type: EventTriggerType.Mousemove,
            coords: action.coords,
        },
        object: getCurrentObject(state, action),
        outcome: {
            type: EventOutcomeType.Miss,
        },
    };
    return makeEvent(event);
}
export function missedClick(action, state) {
    var event = {
        trigger: {
            type: EventTriggerType.Tap,
            coords: action.coords,
        },
        object: getCurrentObject(state, action),
        outcome: {
            type: EventOutcomeType.Miss,
        },
    };
    return makeEvent(event);
}
function getActionTrigger(action, state, expectedTriggers) {
    if (!action.meta) {
        throw new Error("Got ".concat(action.type, " but don't know what triggered it"));
    }
    if (expectedTriggers.indexOf('hotspot') !== -1 && action.meta.hotspotID) {
        var hotspot = getHotspotByID(state, action.meta.hotspotID);
        return {
            trigger: getHotspotTrigger(hotspot.action, action),
            object: {
                type: 'hotspot',
                id: hotspot.id,
            },
        };
    }
    if (expectedTriggers.indexOf('timer') !== -1 && action.meta.timer) {
        return {
            trigger: {
                type: 'timer',
            },
            object: {
                type: 'player',
            },
        };
    }
    if (expectedTriggers.indexOf(EventOutcomeType.Miss) !== -1 &&
        action.meta.miss) {
        var _a = getCurrentScreen(state), id = _a.id, _b = _a.img, width = _b.width, height = _b.height;
        return {
            trigger: {
                type: EventTriggerType.Tap,
                coords: action.meta.miss.coords,
            },
            object: {
                type: 'screen',
                id: id,
                width: width,
                height: height,
            },
        };
    }
    if (expectedTriggers.indexOf('hoverOut') !== -1 && action.meta.hoverOut) {
        return {
            trigger: {
                type: EventTriggerType.Mousemove,
                coords: action.meta.coords,
            },
            object: {
                type: 'hotspot',
                id: action.meta.hotspotID,
            },
        };
    }
    if (expectedTriggers.indexOf('swipeNavigation') !== -1 &&
        action.meta.swipeNavigation) {
        return {
            trigger: {
                type: 'player',
            },
            object: {
                type: 'player',
            },
        };
    }
    throw new Error("Got ".concat(action.type, " but don't know what triggered it"));
}
function getHotspotTrigger(hotspotType, action) {
    var type = getHotspotTriggerType(hotspotType);
    var _a = action.meta, coords = _a.coords, duration = _a.duration, endCoords = _a.endCoords, startCoords = _a.startCoords;
    switch (hotspotType) {
        case PINCH_IN:
            return { coords: coords, direction: 'in', duration: duration, type: type };
        case PINCH_OUT:
            return { coords: coords, direction: 'out', duration: duration, type: type };
        case SWIPE_DOWN:
            return { endCoords: endCoords, direction: 'down', duration: duration, startCoords: startCoords, type: type };
        case SWIPE_LEFT:
            return { endCoords: endCoords, direction: 'left', duration: duration, startCoords: startCoords, type: type };
        case SWIPE_RIGHT:
            return { endCoords: endCoords, direction: 'right', duration: duration, startCoords: startCoords, type: type };
        case SWIPE_UP:
            return { endCoords: endCoords, direction: 'up', duration: duration, startCoords: startCoords, type: type };
        default:
            return { type: type, coords: coords };
    }
}
function getHotspotTriggerType(hotspotType) {
    switch (hotspotType) {
        case CLICK:
            return EventTriggerType.Tap;
        case HOVER:
            return EventTriggerType.Mousemove;
        case PINCH_IN:
        case PINCH_OUT:
            return 'pinch';
        case SWIPE_DOWN:
        case SWIPE_LEFT:
        case SWIPE_RIGHT:
        case SWIPE_UP:
            return 'swipe';
        default:
            return hotspotType;
    }
}
function getCurrentObject(state, action) {
    var _a, _b;
    if (action.layer.type === SCROLL_CONTAINER_LAYER) {
        return action.layer;
    }
    var currentScreen = getCurrentScreen(state);
    var currentOverlay = getCurrentOverlayScreen(state);
    var type;
    var id;
    var height;
    var width;
    if (currentOverlay) {
        type = 'overlay';
        (id = currentOverlay.id);
        (_a = currentOverlay.img, width = _a.width, height = _a.height);
    }
    else {
        type = 'screen';
        (id = currentScreen.id);
        (_b = currentScreen.img, width = _b.width, height = _b.height);
    }
    return {
        type: type,
        id: id,
        height: height,
        width: width,
    };
}
export function makeEvent(event, overrideTimestamp) {
    var prevId = currentEventId;
    var id = flake.gen();
    var fullEvent = __assign(__assign({}, event), { prevId: prevId, id: id, timestamp: overrideTimestamp || getTimestamp() });
    assertValidEvent(fullEvent);
    // only set the currentEventId if the event validates
    currentEventId = id;
    return fullEvent;
}
export function getTimestamp() {
    if (startTime === null) {
        startTime = new Date();
        return 0;
    }
    return new Date() - startTime;
}
export function resetTimestamp() {
    startTime = null;
}
function getOverlayAnimation(state) {
    var transition = getOverlayProps(state, true).transition;
    if (!transition) {
        return { animation: null, reverseAnimation: false };
    }
    var animation = transition.split('-');
    var direction = animation.pop();
    animation = getCleanAnimationName(animation.join(''));
    return { animation: animation, reverseAnimation: direction === 'leave' };
}
// These badly named animations have been an issue for many years!
var cleanedAnimationNames = {
    fade: 'fade',
    pop: 'pop',
    flip: 'flip',
    flow: 'flow',
    pushright: 'push-left',
    pushleft: 'push-right',
    slide: 'slide-left',
    'slide-left': 'slide-right',
    slideup: 'slide-up',
    slidedown: 'slide-down',
    slidefade: 'slide-fade',
};
function getCleanAnimationName(animation) {
    return cleanedAnimationNames[animation] || null;
}
export function getBrokenTransitionName(animation, isReversed) {
    if (!animation) {
        return animation;
    }
    var originalName = findKey(cleanedAnimationNames, function (t) { return t === animation; });
    if (isReversed) {
        originalName += '-reversed';
    }
    return originalName;
}
function getOverlayCoords(state, hotspot, action) {
    if (hotspot.overlayPosition === OverlayPosition.Custom) {
        // Nice and easy -- the hotspot _already_ gives the overlay position relative to the base image
        return [hotspot.overlayX, hotspot.overlayY];
    }
    // Comppute the position in the viewport then convert to image position
    var overlayScreen = getCurrentOverlayScreen(state);
    var currentScreen = getCurrentScreen(state);
    var marginLeft = getScreenStyles(state, true).imageMargin.marginLeft;
    var screenScrollPos;
    if (!action.meta) {
        // must be navigation event which doesn't have the expected meta data so
        // query the DOM directly. At some point we'll tidy up these hardcoded ids
        screenScrollPos = document.getElementById(CURRENT_SCREEN_ID).scrollTop;
    }
    else {
        screenScrollPos = action.meta.screenScrollPos.current;
    }
    var _a = getPositionStyles({
        positionCode: hotspot.overlayPosition,
        width: scale(state, overlayScreen.img.width),
        height: scale(state, overlayScreen.img.height),
        screenWidth: scale(state, currentScreen.img.width),
        screenHeight: state.screen.renderedDimensions.height,
        left: scale(state, hotspot.overlayX),
        top: scale(state, hotspot.overlayY),
        scrollTop: screenScrollPos,
        statusBarTrimHeight: 0, // does this matter? statusBarTrimHeight
    }), top = _a.top, left = _a.left;
    return viewportToImage(state, { type: SCREEN_LAYER }, { x: left + marginLeft, y: top + screenScrollPos });
}
export function unscale(state, value) {
    var _scale = getCurrentScale(state);
    // TODO remove this hack to get actual scale value
    var actualScale = _scale(100000) / 100000;
    // ////////////////////////////////////////////////
    return round(value / actualScale);
}
function scale(state, value) {
    return (100000 / unscale(state, 100000)) * value;
}
